import { calculateChartRange, type MetricChartSpec } from '@/components/charts/generic';
import type { SwimSpeedDataPoint, ThresholdRange } from '@/domain/behavior';
import { getSwimSpeedUnitLabelByLocale, type Locale } from '@/utils/localeFormatters';

import {
    BEHAVIOR_GREEN,
    BEHAVIOR_LINE_MARKER,
    BEHAVIOR_LINE_TENSION,
    behaviorPoints,
    behaviorXAxis,
    measurementsFooter,
    swimSpeedGradient,
} from '../behaviorChartSpec';
import { type BehaviorContext, contextSeries } from '../contextSeries/contextSeries';

/** The axis reaches at least this far, so a slow stretch does not fill the plot. */
const SWIM_SPEED_MIN_MAX = 0.8;

export interface SwimSpeedSpecInput {
    points: SwimSpeedDataPoint[];
    threshold: ThresholdRange;
    t: (key: string) => string;
    locale: Locale;
    /** The reading on the right axis; none on the dashboard. */
    context?: BehaviorContext;
    /** Plots the active fish's swim speed and counts their measurements. */
    activeOnly?: boolean;
}

/**
 * Swim speed in body lengths a second, coloured by its threshold band, with the day's
 * measurement count in the tooltip footer and, on the swim speed page, a context band on the
 * right. The axis fits the speeds from 0 up.
 */
export function buildSwimSpeedSpec(input: SwimSpeedSpecInput): MetricChartSpec<'line'> {
    const { points, threshold, t, locale, context, activeOnly = false } = input;
    const speed = (point: SwimSpeedDataPoint) =>
        activeOnly ? point.activeSwimSpeed : point.swimSpeed;
    const range = calculateChartRange(points.map(speed), 15, true, SWIM_SPEED_MIN_MAX);
    const right = context ? contextSeries(points, context, t) : undefined;

    return {
        kind: 'line',
        ...behaviorXAxis(points, locale),
        series: [
            {
                key: activeOnly ? 'activeSwimSpeed' : 'swimSpeed',
                label: activeOnly ? t('overview.activeSwimSpeed') : t('common.swimSpeed'),
                color: BEHAVIOR_GREEN,
                lineGradient: swimSpeedGradient(threshold),
                tension: BEHAVIOR_LINE_TENSION,
                marker: BEHAVIOR_LINE_MARKER,
                data: behaviorPoints(points, speed),
            },
            ...(right ? [right.series] : []),
        ],
        left: {
            label: t('common.swimSpeed'),
            unit: getSwimSpeedUnitLabelByLocale(locale),
            ...range,
        },
        ...(right ? { right: right.axis } : {}),
        locale,
        tooltipFooterLines: measurementsFooter(
            points,
            (point) => (activeOnly ? point.numberOfActiveMeasurements : point.numberOfMeasurements),
            () => t('common.measurements'),
            locale
        ),
    };
}
